import React from 'react';
import { Box, Typography } from '@mui/material';
import styles from './ComparisonBar.module.css';

/**
 * Componente de Comparación de Barras de Progreso.
 * @param {string} parameter - Nombre del parámetro de comparación.
 * @param {string} left - Valor de la barra izquierda.
 * @param {string} right - Valor de la barra derecha.
 */
const ComparisonBarComponent = ({ parameter, left, right, metricTotal = null, isHigherBetter = true }) => {
    /**
     * Calcula el porcentaje para las barras.
     * @param {string} amount
     * @param {string} rest
     * @returns {string}
     */
    const getPercent = (amount, rest) => {
        const leftNumber = parseFloat(amount) || 0;
        const rightNumber = parseFloat(rest) || 0;
        const total = metricTotal ?? leftNumber + rightNumber;
        return total === 0 ? '0%' : `${(leftNumber / total) * 100}%`;
    };

    const winnerColor = '#206945';
    const loserColor = '#963c3c';
    const tieColor = '#B7410E';

    const getColor = (item, rival) => {
        if (item === rival) return tieColor;

        if (isHigherBetter && item > rival) {
            return winnerColor;
        } else if (!isHigherBetter && item < rival) {
            return winnerColor;
        } else {
            return loserColor;
        }
    };

    return (
        <Box className={styles.multipleProgressBarContainer} width={'100%'}>
            {/* Parámetro (encabezado con aria) */}
            <Typography aria-label={`Comparativa de ${parameter}`} variant="h3" mb={0} fontSize={12}>
                {parameter}
            </Typography>

            {/* Contenedor principal de la barra */}
            <Box className={styles.progressBarContainer} width={'100%'}>
                {/* Número Izquierdo */}
                <Typography
                    className={styles.progressBarLeftSideNumber}
                    aria-label={`Valor izquierdo: ${left}`}
                    fontSize={12}
                >
                    {left}
                </Typography>

                {/* Barra Izquierda */}
                <Box className={styles.progressBarLeftSide} sx={{ width: `calc((100% - 100px) / 2)` }}>
                    <Box
                        className={styles.progressBarLeftSideArea}
                        role="progressbar"
                        aria-valuenow={parseFloat(left) || 0}
                        aria-valuemin={0}
                        aria-valuemax={parseFloat(left) + parseFloat(right)}
                        aria-label={`${parameter} lado izquierdo`}
                        sx={{
                            width: getPercent(left, right),
                            backgroundColor: getColor(left, right),
                        }}
                    ></Box>
                </Box>

                {/* Barra Derecha */}
                <Box className={styles.progressBarRightSide} sx={{ width: `calc((100% - 100px) / 2)` }}>
                    <Box
                        className={styles.progressBarRightSideArea}
                        role="progressbar"
                        aria-valuenow={parseFloat(right) || 0}
                        aria-valuemin={0}
                        aria-valuemax={parseFloat(left) + parseFloat(right)}
                        aria-label={`${parameter} lado derecho`}
                        sx={{
                            width: getPercent(right, left),
                            backgroundColor: getColor(right, left),
                        }}
                    ></Box>
                </Box>

                {/* Número Derecho */}
                <Typography
                    className={styles.progressBarRightSideNumber}
                    aria-label={`Valor derecho: ${right}`}
                    fontSize={12}
                >
                    {right}
                </Typography>
            </Box>

            {/* Descripción accesible para SEO */}
            <Typography variant="body2" className={styles.srOnly}>
                Comparación de {parameter}: lado izquierdo con un valor de {left} y lado derecho con un valor
                de {right}.
            </Typography>
        </Box>
    );
};

export default ComparisonBarComponent;
